import { useEffect, memo, useState } from 'react';
import {
    Box,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Switch,
    Select,
    MenuItem,
    TextField,
    TableContainer,
    Paper,
    TablePagination,
    IconButton,
    Alert,
    Typography,
    Tooltip
} from "@mui/material";
import moment from "moment";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import DeleteIcon from '@mui/icons-material/Delete';
import UndoIcon from '@mui/icons-material/Undo';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HistoryIcon from '@mui/icons-material/History';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { formatUtcDate, formatUtcDateTime } from '../../../utils/dateTime';
import ConfirmDialog from '../../../components/ConfirmDialog';

function PriceColumnHeader({ showMessage, onOpenMessage }) {
    return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "primary.dark", mr: 1 }}>
                    Liquidating Price
                </Typography>
                {!showMessage && (
                    <IconButton
                        size="small"
                        onClick={onOpenMessage}
                        sx={{ color: "primary.main" }}
                    >
                        <VisibilityIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                )}
            </Box>
        </Box>
    );
}

const columns = [
    { key: "id", label: "Id" },
    { key: "productItemId", label: "ProductItem Id" },
    { key: "productItemName", label: "ProductItem Name" },
    { key: "stockQty", label: "Stock Qty" },
    { key: "price", label: "Liquidating Price" },
    { key: "discountPercentage", label: "Discount on SP" },
    { key: "qty", label: "Sale Qty" },
    { key: "soldQty", label: "Sold Qty" },
    { key: "startDate", label: "Start Date", },
    { key: "startTime", label: "Start Time" },
    { key: "stoppageCriteria", label: "Stoppage Criteria" },
    { key: "enabled", label: "Enable/Disable" },
    { key: "timeCreated", label: "Created At" },
    { key: "timeUpdated", label: "Last Updated At" },
    { key: "action", label: "Action" }
];

const getTimeValue = (value) => {
    if (!value) return null;
    return moment.isMoment(value) ? value : moment(value, "HH:mm:ss");
};

const getLiquidatingPrice = (row) => {
    if (row.applicableSellingPrice !== null && row.applicableSellingPrice !== undefined) {
        const hasSellingPrice = row.sellingPrice !== null && row.sellingPrice !== undefined;

        if (!hasSellingPrice) {
            return `${row.applicableSellingPrice}`;
        }

        return (
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.8 }}>
                <Box component="span" sx={{ textDecoration: "line-through", color: "red" }}>
                    {row.sellingPrice}
                </Box>
                <Box component="span" sx={{ color: "green", position: "relative", bottom: -3 }}>{row.applicableSellingPrice}</Box>
            </Box>
        );
    }
    return "-";
};

// Memoized per row: with hundreds of SKUs, editing one row's qty/time/switch
// used to re-render the whole table body (and recompute every other row's
// price/status) on every keystroke. Now only the edited row re-renders,
// since the parent's onChange updates rows immutably (unaffected rows keep
// the same object reference and this memo bails out for them).
const LiquidationRow = memo(function LiquidationRow({
    row,
    index,
    timezone,
    stoppageCriteriaEnums,
    handleChange,
    onRemove,
    onRevert,
    navigate,
    setQtyConfirm,
}) {
    const hasQtyError =
        row.enabled && (row.isAdded || row.isUpdated) &&
        (row.qty === undefined || row.qty === null || row.qty === "" || Number(row.qty) <= 0);
    const hasUnsavedChanges = row.isAdded || row.isUpdated;

    return (
        <TableRow
            hover
            sx={{
                backgroundColor: hasUnsavedChanges ? "rgba(255, 193, 7, 0.1)" : row.permanentlyDeactivated ? "error" : "inherit",
                "&:nth-of-type(even)": {
                    backgroundColor: hasUnsavedChanges ? "rgba(255, 193, 7, 0.1)" : row.permanentlyDeactivated ? "error" : "action.hover",
                },
                transition: "all 0.2s ease",
                opacity: row.permanentlyDeactivated ? 0.75 : 1,
                "& .MuiTableCell-root": row.permanentlyDeactivated ? {
                    position: "relative",
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        right: 0,
                        top: "50%",
                        borderTop: "2px solid",
                        borderColor: "error.main",
                        pointerEvents: "none",
                    }
                } : undefined,
            }}
        >
            {columns.map((column) => {
                switch (column.key) {
                    case "price":
                        return (
                            <TableCell key={column.key}>
                                {getLiquidatingPrice(row)}
                            </TableCell>
                        );
                    case "startTime":
                        return (
                            <TableCell key={column.key}>
                                <TimePicker
                                    value={getTimeValue(row.startTime)}
                                    onChange={(val) => {
                                        if (moment(val).isBefore(moment()) || row.permanentlyDeactivated) return;
                                        handleChange(
                                            index,
                                            "startTime",
                                            val ? val.format("HH:mm:ss") : null
                                        )
                                    }}
                                    minTime={row.isAdded ? moment() : null}
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            error: row.isAdded && moment(row.startTime).isBefore(moment()),
                                            sx: { width: 150 },
                                        }
                                    }}
                                /> </TableCell>
                        );
                    case "stoppageCriteria":
                        return (
                            <TableCell key={column.key}>
                                <Select
                                    size="small"
                                    value={row.stoppageCriteria ?? "EOD"}
                                    onChange={(e) => {
                                        if (row.permanentlyDeactivated) return;
                                        handleChange(index, "stoppageCriteria", e.target.value)
                                    }}
                                >
                                    {stoppageCriteriaEnums.map((criteria) => (
                                        <MenuItem key={criteria} value={criteria}>
                                            {criteria === "EOD" ? "End Of Day" : criteria === "EOFSTOCK" ? "End Of Stock" : criteria}
                                        </MenuItem>
                                    ))}
                                </Select> </TableCell>
                        );
                    case "enabled":
                        return (
                            <TableCell key={column.key}>
                                <Switch
                                    checked={Boolean(row.enabled)}
                                    onChange={(e) => {
                                        if (row.permanentlyDeactivated) return;
                                        handleChange(index, "enabled", e.target.checked)
                                    }}
                                />
                            </TableCell>
                        );
                    case "startDate":
                        return (
                            <TableCell key={column.key}>
                                {formatUtcDate(row.startDate, timezone)}
                            </TableCell>
                        );
                    case "timeCreated":
                    case "timeUpdated":
                        return (
                            <TableCell key={column.key}>
                                <Tooltip title={formatUtcDateTime(row[column.key], timezone)}>
                                    <span>{formatUtcDate(row[column.key], timezone)}</span>
                                </Tooltip>
                            </TableCell>
                        );
                    case "action":
                        return (
                            <TableCell key={column.key} sx={{ width: 100, minWidth: 100 }}>
                                {!(row.isUpdated && !row.isAdded) && (!row.permanentlyDeactivated) && (
                                    <IconButton
                                        size="small"
                                        onClick={() => { onRemove(row) }}
                                        color={"error"}
                                    >
                                        <DeleteIcon />
                                    </IconButton>

                                )}
                                {((row.isUpdated && !row.isAdded) || row.permanentlyDeactivated) && (
                                    <IconButton
                                        color="primary"
                                        size="small"
                                        onClick={() => onRevert(index)}
                                    >
                                        <UndoIcon />

                                    </IconButton>
                                )}
                                {row.id && (
                                    <Tooltip title="View History">
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            aria-label="history"
                                            onClick={() => navigate(`/audit?entityId=${row.id}&entityType=FLASH_SALE`)}
                                        >
                                            <HistoryIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}

                            </TableCell>
                        );
                    case "discountPercentage":
                        return (
                            <TableCell key={column.key}>
                                {row.discountPercentage != null ? `${row.discountPercentage}%` : "-"}
                            </TableCell>
                        );
                    case "qty":
                        return (
                            <TableCell key={column.key}>
                                <TextField
                                    size="small"
                                    type="number"
                                    value={row.qty ?? ""}
                                    onChange={(e) => {
                                        if (row.permanentlyDeactivated) return;
                                        const newQty = e.target.value === "" ? "" : Math.max(1, Number(e.target.value));
                                        if (newQty !== "" && row.stockQty && Number(newQty) > Number(row.stockQty) * 0.5) {
                                            setQtyConfirm({ open: true, index, value: newQty });
                                            return;
                                        }
                                        handleChange(index, "qty", newQty);
                                    }}
                                    style={{ width: '100px' }}
                                    error={hasQtyError}
                                    helperText={hasQtyError ? "Required" : " "}
                                />
                            </TableCell>
                        );
                    default: return (
                        <TableCell key={column.key}>
                            {row[column.key] ?? "-"}
                        </TableCell>
                    );
                }
            })}
        </TableRow>
    );
});

export default function LiquidationSaleProductList({
    rows,
    onChange: handleChange,
    page,
    setPage,
    size,
    setSize,
    totalElements,
    stoppageCriteriaEnums,
    onRemove,
    onRevert
}) {
    const [showPriceMessage, setShowPriceMessage] = useState(false);
    const [qtyConfirm, setQtyConfirm] = useState({ open: false, index: null, value: null });
    const timezone = useSelector((state) => state.globalData.timezone);
    const navigate = useNavigate();

    useEffect(() => {
        if (!showPriceMessage) return undefined;

        const timeoutId = setTimeout(() => {
            setShowPriceMessage(false);
        }, 10000);

        return () => clearTimeout(timeoutId);
    }, [showPriceMessage]);

    const missingPriceItemIds = [...new Set(
        rows
            .filter((row) => row?.applicableSellingPrice == null)
            .map((row) => row?.productItemId ?? row?.id)
            .filter(Boolean)
    )];

    const hasMissingLiquidationPrice = missingPriceItemIds.length > 0;


    return (
        <>
            {hasMissingLiquidationPrice && (
                <Alert severity="warning" sx={{ m: 1, mb: 2 }}>
                    Liquidating price is not available for productItemId:{" "}
                    {missingPriceItemIds.join(", ")}. Please contact Geo Head.
                </Alert>
            )}

            <Paper
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 3,
                    overflow: "hidden",
                }}
            >


                <TableContainer
                    sx={{
                        flex: 1,
                        overflow: "auto",
                    }}
                >
                    <Table
                        stickyHeader
                        size="small"
                        sx={{
                            minWidth: 1400,
                            "& .MuiTableCell-root": {
                                py: 1.5,
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                {columns.map((column, ind) => (
                                    <TableCell
                                        key={ind}
                                        align="left"
                                        sx={{
                                            color: "primary.main",
                                            fontWeight: 600,
                                            whiteSpace: column.key === "price" ? "normal" : "nowrap",
                                            verticalAlign: "top",
                                        }}
                                    >
                                        {column.key === "price" ? (
                                            <PriceColumnHeader
                                                showMessage={showPriceMessage}
                                                onOpenMessage={() => setShowPriceMessage(true)}
                                            />
                                        ) : (
                                            column.label
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                            {showPriceMessage && (
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell colSpan={13} sx={{ py: 1, px: 2 }}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                gap: 1,
                                                borderRadius: 1,
                                                px: 1,
                                                py: 1,
                                                backgroundColor: "rgba(25, 118, 210, 0.08)",
                                                width: "fit-content"
                                            }}
                                        >
                                            <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                                                Liquidation price shown is based on current selling price and may change when selling price updates.
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                onClick={() => setShowPriceMessage(false)}
                                                sx={{ color: "text.secondary", p: 0 }}
                                            >
                                                ✕
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>)}

                        </TableHead>

                        <TableBody>
                            {rows.map((row, index) => (
                                <LiquidationRow
                                    key={row.id ?? row.productItemId ?? index}
                                    row={row}
                                    index={index}
                                    timezone={timezone}
                                    stoppageCriteriaEnums={stoppageCriteriaEnums}
                                    handleChange={handleChange}
                                    onRemove={onRemove}
                                    onRevert={onRevert}
                                    navigate={navigate}
                                    setQtyConfirm={setQtyConfirm}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[5, 10, 20, 50]}
                    component="div"
                    count={totalElements}
                    sx={{
                        borderTop: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "#fff",
                    }}
                    page={page}
                    onPageChange={(event, newPage) => {
                        setPage(newPage);
                    }}
                    rowsPerPage={size}
                    onRowsPerPageChange={(event) => {
                        setSize(parseInt(event.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>

            <ConfirmDialog
                open={qtyConfirm.open}
                title="Confirm Stock Usage"
                message="The sale quantity exceeds 50% of the available stock. Do you want to proceed?"
                confirmText="Yes, Continue"
                cancelText="Cancel"
                onClose={() => setQtyConfirm({ open: false, index: null, value: null })}
                onConfirm={() => {
                    handleChange(qtyConfirm.index, "qty", qtyConfirm.value);
                    setQtyConfirm({ open: false, index: null, value: null });
                }}
            />
        </>
    );
}
